import React from 'react';
import { RenderDescription } from './types/Tools';
import { Text, CheckBox, Input, Icon, Card } from 'react-native-elements';
import { View } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import TouchableWrapper from '../components/TouchableWrapper';
import RNPickerSelect from 'react-native-picker-select';
import { colors } from './constants';

type RendererType = {
	question: RenderDescription;
	index: number;
	allowNumbering: boolean;
	beginNumberingAt: number;
};
export default function Renderer({ question, index, allowNumbering, beginNumberingAt }: RendererType): JSX.Element {
	return (
		<Card containerStyle={{ marginBottom: 20 }}>
			<Text style={{ fontSize: 18 }}>
				{allowNumbering && beginNumberingAt - 1 <= index ? `${index + 2 - beginNumberingAt}. ` : ''} {question.content}{' '}
				{question.required ? <Text style={{ fontWeight: '700', color: 'red' }}>*</Text> : <></>}
			</Text>
			{RenderOptions(question)}
		</Card>
	);
}
type AdminRendererType = {
	question: RenderDescription;
	index: number;
	allowNumbering: boolean;
	beginNumberingAt: number;
	edit?: (index: number) => void;
	del?: (index: number) => void;
	drag?: () => void;
};
export function AdminRenderer({
	question,
	index,
	allowNumbering,
	beginNumberingAt,
	edit,
	del,
	drag,
}: AdminRendererType): JSX.Element {
	return (
		<Card containerStyle={{ marginBottom: 20 }}>
			<Text style={{ fontSize: 18 }}>
				{allowNumbering && beginNumberingAt - 1 <= index ? `${index + 2 - beginNumberingAt}. ` : ''} {question.content}{' '}
				{question.required ? <Text style={{ fontWeight: '700', color: 'red' }}>*</Text> : <></>}
			</Text>
			{edit ? (
				<View style={{ flexDirection: 'row', position: 'absolute', right: 0 }}>
					<Icon
						reverse
						name="edit-3"
						type="feather"
						size={16}
						color={colors.green}
						onPress={() => {
							edit(index);
						}}
					/>
					{del ? (
						<Icon
							reverse
							name="trash-2"
							type="feather"
							size={16}
							color={colors.red}
							onPress={() => {
								del(index);
							}}
						/>
					) : (
						<></>
					)}
					{drag ? <Icon reverse name="move" type="feather" size={16} color={colors.blue} onLongPress={drag} /> : <></>}
				</View>
			) : (
				<></>
			)}
			{RenderOptions(question)}
		</Card>
	);
}

function RenderOptions(question: RenderDescription) {
	const { type, options, selections } = question;
	switch (type) {
		case 'single-choice':
			return <SingleChoice options={options} selections={selections} />;
		case 'multi-choice':
			return <MultiChoice options={options} selections={selections} />;
		case 'text':
			return <TextInputChoice type={type} selections={selections} />;
		case 'number':
			return <TextInputChoice type={type} selections={selections} />;
		case 'date':
			return <DateTimePickerChoice selections={selections} />;
		case 'list':
			return <ListChoice options={options} selections={selections} />;
		default:
			break;
	}
}

interface ListChoiceProps {
	options: Array<string>;
	selections: Array<string>;
}
function ListChoice({ options, selections }: ListChoiceProps): JSX.Element {
	const [selected, setSelected] = React.useState('');
	// const [show, setShow] = React.useState(false);
	return (
		<View style={{ marginVertical: 10 }}>
			<RNPickerSelect
				value={selected}
				onValueChange={(itemValue) => {
					setSelected(itemValue.toString());
					selections[0] = itemValue.toString();
				}}
				placeholder={{ label: 'Select option', value: undefined }}
				items={options.map((option) => ({ label: option, value: option }))}
				style={{
					inputIOS: {
						backgroundColor: '#00000000',
						paddingVertical: 12,
						paddingHorizontal: 10,
						marginTop: 10,
						borderWidth: 0,
						borderColor: 'gray',
						borderRadius: 4,
						color: 'black',
						paddingRight: 30, // to ensure the text is never behind the icon
					},
					inputAndroid: {
						backgroundColor: '#00000000',
						paddingHorizontal: 10,
						paddingVertical: 8,
						marginTop: 10,
						borderWidth: 0,
						borderColor: '#c5c5c5',
						borderBottomWidth: 1,
						borderRadius: 0,
						color: 'black',
						paddingRight: 30, // to ensure the text is never behind the icon
					},
				}}
			/>
		</View>
	);
}

interface DateTimePickerChoiceProps {
	selections: Array<string>;
}
function DateTimePickerChoice({ selections }: DateTimePickerChoiceProps): JSX.Element {
	const [date, setDate] = React.useState(new Date());
	const [show, setShow] = React.useState(false);
	return (
		<View style={{ marginVertical: 10 }}>
			<TouchableWrapper
				style={{ flex: 1 }}
				onPress={() => {
					setShow(!show);
				}}
			>
				<Input value={date.toDateString()} editable={false} containerStyle={{ marginTop: 10 }} />
			</TouchableWrapper>
			{show && (
				<DateTimePicker
					testID="dateTimePicker"
					value={date}
					display="default"
					onChange={(event, selectedDate) => {
						const currentDate = selectedDate || date;
						setShow(!show);
						setDate(currentDate);
						selections[0] = currentDate.toDateString();
					}}
				/>
			)}
		</View>
	);
}

interface TextInputChoiceProps {
	type: 'text' | 'number';
	selections: Array<string>;
}
function TextInputChoice({ type, selections }: TextInputChoiceProps): JSX.Element {
	const [text, setText] = React.useState('');
	return (
		<View style={{ marginVertical: 10 }}>
			<Input
				value={text}
				onChangeText={(text) => {
					setText(text);
					selections[0] = text;
				}}
				keyboardType={type == 'number' ? 'number-pad' : 'default'}
			/>
		</View>
	);
}

interface ChoiceProps {
	options: Array<string>;
	selections: Array<string>;
}
function SingleChoice({ options, selections }: ChoiceProps): JSX.Element {
	const [choice, setChoice] = React.useState('');
	const checkboxes = options.map((option, index) => (
		<CheckBox
			key={`${option}${index}`}
			title={option}
			checked={choice === option}
			checkedIcon="dot-circle-o"
			uncheckedIcon="circle-o"
			onPress={() => {
				setChoice(option);
				selections[0] = option;
			}}
			containerStyle={{ borderWidth: 0, backgroundColor: '#00000000' }}
		/>
	));
	return <View style={{ marginVertical: 10 }}>{checkboxes}</View>;
}

function MultiChoice({ options, selections }: ChoiceProps): JSX.Element {
	const [choice, setChoice] = React.useState<Array<string>>([]);
	const checkboxes = options.map((option, index) => (
		<CheckBox
			key={`${option}${index}`}
			title={option}
			checked={choice?.indexOf(option) >= 0}
			onPress={() => {
				// really manual to force reactivity
				const ind = choice.indexOf(option);
				if (ind < 0) {
					const newSelections = [...choice, ...[option]];
					setChoice(newSelections);
					newSelections.forEach((s, i) => {
						selections[i] = s;
					});
				} else {
					selections.splice(ind, 1);
					const newSelections: Array<string> = [];
					selections.forEach((s, i) => {
						newSelections[i] = s;
					});
					setChoice(newSelections);
				}
			}}
			containerStyle={{ borderWidth: 0, backgroundColor: '#00000000' }}
		/>
	));
	return <View style={{ marginVertical: 10 }}>{checkboxes}</View>;
}
